import { Toolbar } from "@mui/material";
import {
    PropsWithChildren,
    ReactElement,
    useCallback,
    useState,
    MouseEvent,
} from "react";
import { TableInstance } from "react-table";
import { TableIconActionButton } from "../button/SmallIconActionButton";

import { mdiFileExport, mdiPencilOutline } from "@mdi/js";
import { mdiDeleteOutline } from "@mdi/js";
import { mdiPlus } from "@mdi/js";
import { mdiPrinter } from "@mdi/js";
import { mdiEye } from "@mdi/js";
import { mdiFilter } from "@mdi/js";
import { TableLabelActionButton } from "../label/LabelActionButton";
import { FilterModal } from "./FilterModal";
import { mdiLockOutline, mdiLockOpenOutline } from "@mdi/js";
import { TableToolbarProps } from "./TableProps";

export function TableToolbar<T extends Record<string, unknown>>({
    instance,
    onAdd,
    onDelete,
    onEdit,
    onView,
    onPrint,
    onExport,
    extraCommands = [],
    inlineLock,
    inlineLockState
}: PropsWithChildren<TableToolbarProps<T>>): ReactElement | null {
    
    const { delayFilterHandler } = instance;

    const [anchorElement, setAnchorElement] = useState<Element | undefined>(undefined);
    
    const [filterOpen, setFilterOpen] = useState(false);

    const openFilterModal = useCallback(
        (event: MouseEvent) => {

            if(!!delayFilterHandler) {
                delayFilterHandler({value: true});
            }

            setAnchorElement(event.currentTarget);
            setFilterOpen(true);
        },
        [setAnchorElement, setFilterOpen]
    );

    const closeFilterModal = useCallback(() => {

        if(!!delayFilterHandler) {
            delayFilterHandler({value: undefined});
        }
        
        setFilterOpen(false);
        setAnchorElement(undefined);
    }, []);

    const showToolbar =
        !!onAdd ||
        !!onEdit ||
        !!onDelete ||
        !!onPrint ||
        !!onExport ||
        !!extraCommands ||
        !!onView ||
        !!inlineLock;

    if (showToolbar) {
        return (
            <Toolbar className="table-toolbar">
                <div className="left-buttons">
                    {onAdd && (
                        <TableIconActionButton<T>
                            instance={instance}
                            icon={mdiPlus}
                            onClick={onAdd}
                            label="Add"
                            enabled={({ state }: TableInstance<T>) =>
                                !state.selectedRowIds ||
                                Object.keys(state.selectedRowIds).length === 0
                            }
                            variant="left"
                        />
                    )}
                    {onEdit && (
                        <TableIconActionButton<T>
                            instance={instance}
                            icon={mdiPencilOutline}
                            onClick={onEdit}
                            label="Edit"
                            enabled={({ state }: TableInstance<T>) =>
                                !state.selectedRowIds ||
                                Object.keys(state.selectedRowIds).length === 1
                            }
                            variant="left"
                        />
                    )}
                    {onDelete && (
                        <TableIconActionButton<T>
                            instance={instance}
                            icon={mdiDeleteOutline}
                            onClick={onDelete}
                            label="Delete"
                            enabled={({ state }: TableInstance<T>) =>
                                !state.selectedRowIds ||
                                Object.keys(state.selectedRowIds).length > 0
                            }
                            variant="left"
                        />
                    )}

                    {onPrint && (
                        <TableIconActionButton<T>
                            instance={instance}
                            icon={mdiPrinter}
                            onClick={onPrint}
                            label="Print"
                            enabled={({ state }: TableInstance<T>) =>
                                !state.selectedRowIds ||
                                (instance.name === "reports-table" &&
                                    Object.keys(state.selectedRowIds).length >= 1) ||
                                (instance.name !== "reports-table" &&
                                    Object.keys(state.selectedRowIds).length === 1)
                            }
                            variant="left"
                        />
                    )}

                    {onExport && (
                        <TableIconActionButton<T>
                            instance={instance}
                            icon={mdiFileExport}
                            onClick={onExport}
                            label="Export"
                            /*
                            enabled={({ state }: TableInstance<T>) =>
                                !state.selectedRowIds ||
                                (instance.name === "export-table" &&
                                    Object.keys(state.selectedRowIds).length >= 1) ||
                                (instance.name !== "export-table" &&
                                    Object.keys(state.selectedRowIds).length === 1)
                            }
                            */
                            variant="left"
                        />
                    )}

                    {onView && (
                        <TableIconActionButton<T>
                            instance={instance}
                            icon={mdiEye}
                            onClick={onView}
                            label="View"
                            enabled={({ state }: TableInstance<T>) =>
                                !state.selectedRowIds ||
                                Object.keys(state.selectedRowIds).length === 1
                            }
                            variant="left"
                        />
                    )}


                    {inlineLock && (
                        <TableIconActionButton<T>
                            instance={instance}
                            icon={inlineLockState? mdiLockOutline : mdiLockOpenOutline}
                            onClick={inlineLock}
                            label="Inline Edit"
                            variant="left"
                        />
                    )}

                    {extraCommands.map((c) => {
                        const { type = "icon" } = c;
                        return type === "icon" ? (
                            <TableIconActionButton<T>
                                key={`command-${c.label}`}
                                instance={instance}
                                icon={c.icon}
                                onClick={c.onClick}
                                label={c.label}
                                enabled={c.enabled}
                                variant="left"
                            />
                        ) : (
                            <TableLabelActionButton<T>
                                key={`command-${c.label}`}
                                instance={instance}
                                icon={c.icon}
                                onClick={c.onClick}
                                label={c.label}
                                enabled={c.enabled}
                            />
                        );
                    })}
                </div>
                <div className="right-buttons">
                    <FilterModal<T>
                        instance={instance}
                        onClose={closeFilterModal}
                        show={filterOpen}
                        anchorElement={anchorElement}
                    />
                    <TableIconActionButton<T>
                        instance={instance}
                        icon={mdiFilter}
                        onClick={() => openFilterModal}
                        label="Filter by columns"
                        variant="right"
                    />
                </div>
            </Toolbar>
        );
    } else {
        return <></>;
    }
}
